import { Dropdown } from '@sqs/rosetta-compositions';
import { Checkbox } from '@sqs/rosetta-elements';
import { Box, Text } from '@sqs/rosetta-primitives';
import { useTheme } from '@sqs/rosetta-styled';
import React, { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { fetchSchedulingInstanceCalendars } from '../../../apis/schedulingInstances';
import { usePlatformBreakpoint } from '../../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../../i18n';
import { PageInfo, PageInfoQueryWithSearch } from '../../../models/PaginatedResponse';
import { SchedulingInstancePermission } from '../../../models/SchedulingInstance';
import { SearchSelectItem } from '../../../models/SearchSelectItem';
import { SearchAndSelect } from '../SearchAndSelect/SearchAndSelect';
import { AddUserToInstanceForm } from '../types/UserInstanceForm';

interface FetchedCalendarResponse {
  data: SearchSelectItem[];
  pageInfo: PageInfo;
}

interface SetUserPermissionsProps {
  readonly showVariantSelector?: boolean;
  readonly customFetchCalendars?: (eid: number, options?: PageInfoQueryWithSearch) => Promise<FetchedCalendarResponse>;
  readonly preloadedItemStore?: number[];
}

const detailedPermissionsList = [SchedulingInstancePermission.EDIT, SchedulingInstancePermission.VIEW];

export const SetUserPermissions = ({
  showVariantSelector = false,
  customFetchCalendars,
  preloadedItemStore = []
}: SetUserPermissionsProps) => {
  const { fontSizes, colors } = useTheme();
  const { T, t } = useI18n();
  const { isMobile } = usePlatformBreakpoint();

  const {
    formState: { errors },
    register,
    setValue,
    watch
  } = useFormContext<AddUserToInstanceForm>();

  const allowExportsRegister = register('allowExports');
  register('permission', { required: t("Please choose permission level", {}, { project: 'enterprise-dashboard' }) });

  const schedulingInstanceId = watch('schedulingInstanceId');
  const selectedPermission = watch('permission') as SchedulingInstancePermission;
  const calendarsWithAccess = watch('items');
  const allowExports = !!watch('allowExports');

  // NOTE: this maxHeight calculation is a near estimate of the height of the rest of the values in the container
  const searchSelectPaddingBottom = isMobile ? '100px' : '';

  const fetchCalendarsDefault = async (
  eid: number,
  options?: PageInfoQueryWithSearch)
  : Promise<FetchedCalendarResponse> => {
    const res = await fetchSchedulingInstanceCalendars(eid, schedulingInstanceId, options);
    const calendars = res.data.map((calendar) => ({
      itemId: calendar.id,
      name: calendar.name,
      checked: calendarsWithAccess.map((c) => c.itemId).includes(calendar.id)
    }));
    return {
      data: calendars,
      pageInfo: res.pageInfo
    };
  };

  const fetchCalendars = customFetchCalendars ?? fetchCalendarsDefault;

  return (
    <Box mt={3}>
      <Text.Label>
        <T project="enterprise-dashboard">{"Permission"}</T>
      </Text.Label>
      <Dropdown
        placement="bottom"
        onChange={(v: string) => setValue('permission', v, { shouldDirty: true })}
        value={selectedPermission}>

        <Dropdown.Option
          value={SchedulingInstancePermission.ADMIN}
          description={
          <Text.Caption>
              <T project="enterprise-dashboard">{"Admins have full access to a scheduling instance with the exception of deleting the instance."}</T>


            </Text.Caption>
          }>

          <Text.Body m={0} fontSize={fontSizes[3]} fontWeight={500}>
            <T project="enterprise-dashboard">{"Admin"}</T>
          </Text.Body>
        </Dropdown.Option>
        <Dropdown.Option
          value={SchedulingInstancePermission.EDIT}
          description={
          <Text.Caption>
              <T project="enterprise-dashboard">{"These users have limited access to view and edit a scheduling instance, which can be further restricted to certain calendars."}</T>



            </Text.Caption>
          }>

          <Text.Body m={0} fontSize={fontSizes[3]} fontWeight={500}>
            <T project="enterprise-dashboard">{"View & Edit"}</T>
          </Text.Body>
        </Dropdown.Option>
        <Dropdown.Option
          value={SchedulingInstancePermission.VIEW}
          description={
          <Text.Caption>
              <T project="enterprise-dashboard">{"These users have limited access to view scheduling instances, which can be further restricted to certain calendars."}</T>



            </Text.Caption>
          }>

          <Text.Body m={0} fontSize={fontSizes[3]} fontWeight={500}>
            <T project="enterprise-dashboard">{"View Only"}</T>
          </Text.Body>
        </Dropdown.Option>
      </Dropdown>
      {errors.permission &&
      <Text.Caption sx={{ color: colors.red[400] }} role="alert">
          {errors.permission.message}
        </Text.Caption>
      }
      {detailedPermissionsList.includes(selectedPermission) && schedulingInstanceId ?
      <Box position="relative">
          <Text.Body fontWeight={500} mt={5} mb={0}>
            <T project="enterprise-dashboard">{"Export Settings"}</T>
          </Text.Body>
          <Text.Body as="label" css={{ display: 'inline-flex' }} mt={2}>
            <Checkbox
            ref={allowExportsRegister.ref}
            name="allowExports"
            checked={allowExports}
            onChange={async (v: boolean, e: ChangeEvent<HTMLInputElement>) => {
              await allowExportsRegister.onChange(e);
            }}
            value={true}
            mr={2} />

            <T project="enterprise-dashboard">{"Allow user to export clients and appointments"}</T>
          </Text.Body>
          <Text.Body fontWeight={500} mt={5}>
            <T project="enterprise-dashboard">{"Calendar Access"}</T>
          </Text.Body>

          <SearchAndSelect
          showVariantSelector={showVariantSelector}
          keyId={schedulingInstanceId}
          searchPlaceholder={t("Search calendars", {}, { project: 'enterprise-dashboard' })}
          allCheckText={t("All Calendars", {}, { project: 'enterprise-dashboard' })}
          emptyText={t("There are no calendars associated with this instance.",

          {}, {
            project: 'enterprise-dashboard' })
          }
          listItems={fetchCalendars}
          additionalPaddingBottom={searchSelectPaddingBottom}
          preloadedItemStore={preloadedItemStore} />

        </Box> :

      <></>
      }
    </Box>);

};